import React from "react";
import { Group, Text, useCombobox, Combobox, ActionIcon } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { shortenText } from "../../utils/formatter";
import { useMediaQuery } from "@mantine/hooks";

type RenderComboboxProps = {
  label: string;
  options: Record<string, string>;
  selectedValue: string | number | null;
  setSelected: (v: string | number | null) => void;
  width?: number | string;
  dropdownWidth?: number;
  showClearButton? : boolean;
  setPage?: (page: number) => void;
};

/**
 *   Generic dropdown renderer used across Filter and FilterMobile.
 * - Supports optional avatars for agents
 * - Has "X" clear button
 * - Shows placeholder when no selection
 */
export function RenderCombobox({
  label,
  options,
  selectedValue,
  setSelected,
  width = 110,
  dropdownWidth = 150,
  showClearButton = true,
  setPage,
}: RenderComboboxProps) {
  const isMobile = useMediaQuery("(max-width: 860px)");
  const combobox = useCombobox();
  const selectedOption = Object.entries(options).find(
    ([key]) => String(key) === String(selectedValue)
  );

  const showClear = showClearButton && selectedValue != null && selectedValue !== "";
  const placeholder =
    selectedOption && isMobile
      ? selectedOption[1]
      : selectedOption
        ? shortenText(selectedOption[1] ?? "", 10)
        : "Select type";

  return (
    <Group gap="xs">
      <Text style={{ color: "var(--mid-dark)", fontWeight: 400, fontSize: 14 }}>
        {label}:
      </Text>

      <Combobox
        onOptionSubmit={(optionValue) => {
          setSelected(optionValue);
          combobox.closeDropdown();
          setPage?.(1);
        }}
        store={combobox}
        position="bottom-start"
        withinPortal={false}
      >
        <Combobox.Target>
          <div
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width,
              minHeight: isMobile ? 38 : 28,
              borderBottom: "2px solid var(--brand-color)",
              backgroundColor: "var(--mid-darker-transp-5)",
              cursor: "pointer",
              padding: "0 4px",
              borderRadius: 2,
              position: "relative",
              textAlign: "center",
            }}
          >
            {/* Text or placeholder */}
            <Text
              style={{
                flex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "center",
                color: selectedOption
                  ? "var(--brand-color)"
                  : "var(--mid-dark)",
                fontSize: 15,
                marginRight: showClear ? 15 : 0,
              }}
            >
              {placeholder}
            </Text>

            {/* X button */}
            {showClear && (
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelected(null);
                  combobox.closeDropdown();
                  setPage?.(1);
                }}
                style={{
                  position: "absolute",
                  right: 2,
                  cursor: "pointer",
                  transition: "opacity 0.15s ease-in-out",
                  opacity: 0.6,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
              >
                <IconX size={14} />
              </ActionIcon>
            )}
          </div>
        </Combobox.Target>

        {/* Dropdown */}
        <Combobox.Dropdown style={{ minWidth: dropdownWidth }}>
          <Combobox.Options>
            {Object.keys(options).length === 0 ? (
              <Combobox.Empty>Nothing found</Combobox.Empty>
            ) : (
              Object.entries(options).map(([key, value]) => (
                <Combobox.Option value={String(key)} key={key}>
                  <Group gap="xs">
                    <Text>{value}</Text>
                  </Group>
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Group>
  );
}
